// Import react elements
import { useContext } from 'react';

// Import API calls (Orders)
import { deleteOrder, updateOrderStatus } from "api/APIStrichCalls";

// Import strich api calls (POD Designs)
import {
    deletePodDesign,
    updatePodDesignDetails
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import { useBatchProcessor } from 'global/hooks/useBatchProcessor';
import { useToast } from "global/hooks/useToast";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Custom hook to manage orders - cancelling, marking, resetting etc.
const useCustomisableTableActions = ({ tableType, tableData, fetchTableData, handleSelectRow, handleSetRowItemActive, handleSetRowItemInactive, setSelectedRows, onClose, designOrTemplate }) => {

    //console.log("tableType: ", tableType);

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Define toast options
    const { toastSuccess, toastError } = useToast();



    // Handle update order status
    const handleOrderStatusChange = (orderID, status) => {

        // Provide confirmation dialog
        DisplayConfirmationDialog(

            "<h3>Are you sure you?</h3> <br/>Update order status to: <b>" + status + "</b> for " + orderID,
            "Update",
            "Cancel",
            async function (is_confirmed) {

                // If user confirms, proceed with update
                if (is_confirmed) {

                    // Update the order status
                    updateStatus(orderID, status);

                }

            }

        );

    };

    // Function to update order status
    const updateStatus = async (orderID, status) => {

        // Create payload
        const payload = {
            id: orderID,
            status: status
        }

        try {

            const response = await updateOrderStatus(token, payload);

            if (response.success) {

                // Refetch the orders
                fetchTableData(orderID);

                // Provide confirmation dialog
                DisplayConfirmationDialog(

                    "Done",
                    "OK",
                    "",
                    async function (is_confirmed) {

                        // If user confirms
                        if (is_confirmed) {

                        }

                    }

                );

            } else {

                // Provide error meassge
                DisplayConfirmationDialog(

                    "Error: " + response.error_message,
                    "OK",
                    "",
                    async function (is_confirmed) {

                        // If user confirms
                        if (is_confirmed) {

                        }

                    }

                );

            }
        }

        catch (error) {

            console.error("API error:", error);

        }

    };

    // Add all the order action functions in here


    
    // Get the data from the batch processor function so we can use it to let the user know what's happened 
    const {
        isLoading,
        progress,
        successfulItems,
        failedItems,
        processBatch
    } = useBatchProcessor();



    // Function to handle the batch actions, collect the results and display end message to the user
    const handleBatchAction = async (selectedOrderIDs, orderActionsConfig) => {

        // Run the batch processor with selected orders and the action config
        const result = await processBatch(selectedOrderIDs, orderActionsConfig);

        // Check if need to show finished message
        if (orderActionsConfig.showFinishedMessage) {

            // Tailored messages
            if (result.allSuccessful) {

                // Check if we need to refetch the orders after the action
                if (orderActionsConfig.refetchTableData) {

                    // Fetch the data
                    if (designOrTemplate) {

                        fetchTableData(designOrTemplate);

                    } else {

                        fetchTableData();

                    }
                       
                };

                // If there is an onClose function and the config says to close the pop up
                if (onClose && orderActionsConfig.closePopUp) {

                    // Close the pop up
                    onClose();

                }
                                
                // Clear the selection if we need to
                if (setSelectedRows) {

                    setSelectedRows(result.failed);
                    
                }

                // Toast success message
                toastSuccess({ title: "Success", message:  `All ${result.actionLabel.toLowerCase()} actions completed successfully.` });

            } else if (result.allFailed) {

                // Keep the failed item IDs selected
                if (setSelectedRows) {

                    setSelectedRows(result.failed.map((failedItem) => failedItem.id));

                }

                // Build a useful description of each failed action
                const failedActionMessages = result.failed.map((failedItem) => {

                    // Return the failed ID and its API error
                    return failedItem.id + ": " + failedItem.errorMessage;

                }).join(", ");

                // Show the failed item IDs and API errors
                toastError({
                    title: "Error",
                    message: `All ${result.actionLabel.toLowerCase()} actions failed. ${failedActionMessages}`,
                    autoCloseMs: 6000
                });

            } else if (result.partialSuccess) {

                // Check if we need to refetch the orders after the action
                if (orderActionsConfig.refetchTableData) {

                    // Fetch the data
                    if (designOrTemplate) {

                        fetchTableData(designOrTemplate);

                    } else {

                        fetchTableData();

                    }

                };

                // Keep only the failed item IDs selected
                if (setSelectedRows) {

                    setSelectedRows(result.failed.map((failedItem) => failedItem.id));

                }

                // Build a useful description of each failed action
                const failedActionMessages = result.failed.map((failedItem) => {

                    // Return the failed ID and its API error
                    return failedItem.id + ": " + failedItem.errorMessage;

                }).join(", ");

                // Display the failed item IDs and API errors
                DisplayConfirmationDialog(

                    `Some ${result.actionLabel.toLowerCase()} actions failed. ${failedActionMessages}`,
                    "OK",
                    "",
                    async function (is_confirmed) {

                        // If user confirms
                        if (is_confirmed) {

                        }

                    }

                );
                
            }

        } 

    };



    // Handles the confirmation first
    const handleConfirmAndRun = (selectedOrderIDs, actionConfig) => {

        // Convert selected IDs to a Set for fast lookups
        const selectedOrderIdSet = new Set(selectedOrderIDs);

        // Get all selected Strich IDs
        const strichIds = tableData
            .filter(
                (row) =>
                    selectedOrderIdSet.has(row.id) &&
                    row.source === "strich"
            )
            .map((row) => row.id);

        // Convert Strich IDs to a Set
        const strichIdSet = new Set(strichIds);

        // Get all selected IDs that are not Strich
        const nonStrichIds = selectedOrderIDs.filter(
            (id) => !strichIdSet.has(id)
        );

        let itemsToAction;

        // If this is a pod designs/templates delete action 
        if (tableType === "PodDesigns" && actionConfig.id === "delete" && strichIds.length > 0) {

            // Then use the list of non strich template ids
            itemsToAction = nonStrichIds;

            // If there's no non strich ids to delete
            if (nonStrichIds.length === 0) {

                // Toast an error 
                toastError({ title: "Error", message: "Cannot delete Strich templates.", autoCloseMs: 6000 });

                // Return early
                return;

            }

        } else {

            // Otherwise use all ids
            itemsToAction = selectedOrderIDs;

        }

        // Check if we want the user to confirm the action before proceeding
        if (actionConfig.confirmAction) { 

            // Create plural 's' if there is more than one ID
            const plural = itemsToAction.length > 1 ? "s" : ""; 

            const listOfIDs = tableType === "PodDesigns" ?
            
                itemsToAction.map(id => {

                    const designName = tableData.find((design) => design.id === id)?.title || id;

                    return designName;

                }).join('<br/> ')
                
                :

                itemsToAction.map(id => id).join('<br/> ');
           
            // Display confirmation
            DisplayConfirmationDialog(

                `<h3>Are you sure?</h3> 
                <br/>
                ${actionConfig.label} the following ${tableType === "Orders" ? "order" : tableType === "PodDesigns" ? designOrTemplate : ""}${plural}: <br/><br/>${listOfIDs} 
                ${strichIds.length > 0  ? "<br/> <br/><span>Please note that Strich Templates cannot be deleted.</span>" : ""}   
                <br/>
                <br/>
                <span class='warning-message'>WARNING: This cannot be undone.</span>`,
                "Yes",
                "Cancel",
                async function (is_confirmed) {

                    // If user confirms, proceed with action
                    if (is_confirmed) {

                        // Handle the batch action
                        handleBatchAction(itemsToAction, actionConfig);

                    }

                }

            );

        // Otherwise just proceed, no confirmation needed
        } else {

            // Handle the batch action
            handleBatchAction(itemsToAction, actionConfig);

        }

    };



    // Create menu options array for Orders - used for dropdown above table, per table row and within orderDetails pop up
    const orderActionsConfig = [
        {
            id: "view", // Unique id
            label: 'View Details', // Title label
            clickFunction: handleConfirmAndRun, // Click function used for table row button
            icon: '✏️', // Optional icon
            disabled: false, // Disabled state
            singleItemAction: true, // Is this action just for single item
            apiFunction: handleSelectRow, // Api or function call
            getApiArgs: (id) => [id], // Api or function props
            confirmAction: false, // Do we need to confirm before doing the action
            showFinishedMessage: false, // Do we need to show a finished results message
            refetchTableData: false, // Do we need to re fetch the orders after the action has completed on all items
            hideOnOrderDetailsPage: true, // So we can hide it on the orderDetailsPage
            closePopUp: false // So we can close the pop up if a close function is passed in
        },
        {
            id: "mark",
            label: 'Mark as...',
            //clickFunction: handleMarkAsShipped,
            icon: '',
            disabled: true,
            singleItemAction: false
        },
        {
            id: "delete",
            label: 'Delete',
            clickFunction: handleConfirmAndRun,
            icon: '🗑️',
            disabled: false,
            color: 'red',
            singleItemAction: false,
            apiFunction: deleteOrder,
            getApiArgs: (id) => {
                const order = tableData.find(order => order.id === id);
                return [token, id, order?.venueorderid];
            },
            confirmAction: true,
            showFinishedMessage: true,
            refetchTableData: true,
            hideOnOrderDetailsPage: false,
            closePopUp: true
        },
    ];


    // Create menu options array for POD Designs / Templates - used for dropdown above table
    const podDesignActionsConfig = [
        {
            id: "view", // Unique id
            label: 'View Details', // Title label
            clickFunction: handleConfirmAndRun, // Click function used for table row button
            icon: '✏️', // Optional icon
            disabled: false, // Disabled state
            singleItemAction: true, // Is this action just for single item
            apiFunction: handleSelectRow, // Api or function call
            getApiArgs: (id) => [id], // Api or function props
            confirmAction: false, // Do we need to confirm before doing the action
            showFinishedMessage: false, // Do we need to show a finished results message
            refetchTableData: false, // Do we need to re fetch the orders after the action has completed on all items
            hideOnOrderDetailsPage: true, // So we can hide it on the orderDetailsPage
            closePopUp: false // So we can close the pop up if a close function is passed in
        },
        {
            id: "show", // Unique id
            label: 'Show On Store', // Title label
            clickFunction: handleConfirmAndRun, // Click function used for table row button
            icon: '✔', // Optional icon
            disabled: designOrTemplate === "template" ? true : false, // Disabled state
            singleItemAction: false, // Is this action just for single item
            apiFunction: updatePodDesignDetails, // Api or function call
             getApiArgs: (id) => {  
                const payload = {id: id, active: "Y"}
                return [token, designOrTemplate, id, payload];
            }, // Api or function props
            confirmAction: false, // Do we need to confirm before doing the action
            showFinishedMessage: true, // Do we need to show a finished results message
            refetchTableData: true, // Do we need to re fetch the orders after the action has completed on all items
            hideOnOrderDetailsPage: true, // So we can hide it on the orderDetailsPage
            closePopUp: false // So we can close the pop up if a close function is passed in
        },
        {
            id: "hide", // Unique id
            label: 'Hide On Store', // Title label
            clickFunction: handleConfirmAndRun, // Click function used for table row button
            icon: '✖', // Optional icon
            disabled: designOrTemplate === "template" ? true : false, // Disabled state
            singleItemAction: false, // Is this action just for single item
            apiFunction: updatePodDesignDetails, // Api or function call
            getApiArgs: (id) => {  
                const payload = {id: id, active: "N"}
                return [token, designOrTemplate, id, payload];
            }, // Api or function props
            confirmAction: false, // Do we need to confirm before doing the action
            showFinishedMessage: true, // Do we need to show a finished results message
            refetchTableData: true, // Do we need to re fetch the orders after the action has completed on all items
            hideOnOrderDetailsPage: true, // So we can hide it on the orderDetailsPage
            closePopUp: false // So we can close the pop up if a close function is passed in
        },
        {
            id: "delete",
            label: 'Delete',
            clickFunction: handleConfirmAndRun,
            icon: '🗑️',
            disabled: false,
            color: 'red',
            singleItemAction: false,
            apiFunction: deletePodDesign,
            getApiArgs: (id) => {  
                return [token, designOrTemplate, id];
            },
            confirmAction: true,
            showFinishedMessage: true,
            refetchTableData: true,
            hideOnOrderDetailsPage: false,
            closePopUp: true
        },

    ];



    // Select the appropriate actions config to use based on the table type i.e. Orders, POD Designs, etc.
    const tableActionsConfig = 
    
        tableType === "Orders" ? orderActionsConfig 

            : tableType === "PodDesigns" ? podDesignActionsConfig 

                : null;

                

    return {
        tableActionsConfig,
        handleOrderStatusChange,
        handleConfirmAndRun,
        isLoading,
        progress,
        successfulItems,
        failedItems,
    };

};

export default useCustomisableTableActions;

